import { type FC, type PropsWithChildren, createContext, useContext } from "react";

export type StyleMode = "legacy" | "token";

export interface ResolveStyleModeOptions {
  legacyPropNames?: string[];
  warnOnLegacyProps?: boolean;
}

const DEFAULT_LEGACY_PROP_NAMES = ["className", "style"];

const WARNED_SESSIONS = new Set<string>();

const StyleModeContext = createContext<StyleMode>("token");

const hasOwn = (obj: object, key: string): boolean => Object.prototype.hasOwnProperty.call(obj, key);

const getLegacyPropNames = (options?: ResolveStyleModeOptions): string[] => {
  const names = options?.legacyPropNames ?? DEFAULT_LEGACY_PROP_NAMES;
  return [...new Set(names)];
};

const warnLegacyOnce = (legacyPropNames: string[]): void => {
  if (process.env.NODE_ENV === "production") {
    return;
  }

  if (legacyPropNames.length === 0) {
    return;
  }

  const key = [...legacyPropNames].sort().join("|");
  if (WARNED_SESSIONS.has(key)) {
    return;
  }

  WARNED_SESSIONS.add(key);
  const propLabel = legacyPropNames.length === 1 ? "prop was provided" : "props were provided";
  console.warn(
    `[tokens] Legacy style mode enabled because ${legacyPropNames.map((name) => `\`${name}\``).join(" and ")} ${propLabel}. `
      + "Passing these props keeps legacy SCSS compatibility but bypasses token styling for this subtree.",
  );
};

export const resetDeprecatedStylePropsWarningForTests = (): void => {
  WARNED_SESSIONS.clear();
};

export const resolveStyleMode = (props: object | null | undefined, options?: ResolveStyleModeOptions): StyleMode => {
  if (!props || typeof props !== "object") {
    return "token";
  }

  const legacyPropNames = getLegacyPropNames(options);
  const usedLegacyPropNames = legacyPropNames.filter((propName) => hasOwn(props, propName));
  const usesLegacyProp = usedLegacyPropNames.length > 0;

  if (usesLegacyProp) {
    if (options?.warnOnLegacyProps !== false) {
      warnLegacyOnce(usedLegacyPropNames);
    }
    return "legacy";
  }

  return "token";
};

export interface StyleModeProviderProps extends PropsWithChildren {
  mode: StyleMode;
}

export const StyleModeProvider: FC<StyleModeProviderProps> = ({ mode, children }) => {
  return <StyleModeContext.Provider value={mode}>{children}</StyleModeContext.Provider>;
};

export const useParentStyleMode = (): StyleMode => useContext(StyleModeContext);

export const useResolvedStyleMode = (props: object | null | undefined, options?: ResolveStyleModeOptions): StyleMode => {
  const parentMode = useParentStyleMode();
  const ownMode = resolveStyleMode(props, options);
  return ownMode === "legacy" || parentMode === "legacy" ? "legacy" : "token";
};
